import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../utils/supabase';
import Icon from '../AppIcon';
import TournamentBracket from './TournamentBracket';

/**
 * TournamentList Component
 * Displays active, upcoming, and past tournaments for a game
 *
 * Usage:
 * <TournamentList gameId="game_uuid" />
 */
export default function TournamentList({ gameId }) {
  const { user } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [filter, setFilter] = useState('active'); // active, upcoming, completed

  useEffect(() => {
    if (gameId) {
      loadTournaments();
    }
  }, [gameId, filter]);

  const loadTournaments = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query based on filter
      let query = supabase
        .from('tournaments')
        .select(`
          id,
          name,
          description,
          status,
          bracket_type,
          start_time,
          end_time,
          max_participants,
          prize_pool,
          metadata,
          created_at
        `)
        .eq('game_id', gameId);

      if (filter === 'active') {
        query = query.eq('status', 'active');
      } else if (filter === 'upcoming') {
        query = query.eq('status', 'pending');
      } else if (filter === 'completed') {
        query = query.eq('status', 'completed');
      }

      query = query.order('start_time', { ascending: filter !== 'completed' });

      const { data: tournamentsData, error: tournamentsError } = await query;
      if (tournamentsError) throw tournamentsError;

      // If user is logged in, get their participation status
      if (user?.id && tournamentsData && tournamentsData.length > 0) {
        const tournamentIds = tournamentsData.map(t => t.id);

        const { data: participants, error: participantsError } = await supabase
          .from('tournament_participants')
          .select('tournament_id, status, eliminated_at')
          .eq('user_id', user.id)
          .in('tournament_id', tournamentIds);

        if (participantsError) throw participantsError;

        // Get participant counts
        const { data: participantCounts, error: countsError } = await supabase
          .from('tournament_participants')
          .select('tournament_id')
          .in('tournament_id', tournamentIds);

        if (countsError) throw countsError;

        const countMap = {};
        (participantCounts || []).forEach(p => {
          countMap[p.tournament_id] = (countMap[p.tournament_id] || 0) + 1;
        });

        const participationMap = {};
        (participants || []).forEach(p => {
          participationMap[p.tournament_id] = p;
        });

        const enriched = tournamentsData.map(tournament => ({
          ...tournament,
          isParticipant: !!participationMap[tournament.id],
          participantStatus: participationMap[tournament.id]?.status,
          participantCount: countMap[tournament.id] || 0
        }));

        setTournaments(enriched);
      } else if (tournamentsData && tournamentsData.length > 0) {
        // Just get counts
        const tournamentIds = tournamentsData.map(t => t.id);
        const { data: participantCounts } = await supabase
          .from('tournament_participants')
          .select('tournament_id')
          .in('tournament_id', tournamentIds);

        const countMap = {};
        (participantCounts || []).forEach(p => {
          countMap[p.tournament_id] = (countMap[p.tournament_id] || 0) + 1;
        });

        setTournaments(tournamentsData.map(t => ({
          ...t,
          participantCount: countMap[t.id] || 0
        })));
      } else {
        // No tournaments at all
        setTournaments([]);
      }

    } catch (err) {
      console.error('Error loading tournaments:', err);
      setError('Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinTournament = async (tournamentId) => {
    if (!user) {
      alert('Please log in to join tournaments');
      return;
    }

    try {
      const response = await fetch('/api/sdk/tournaments/join', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tournamentId: tournamentId,
          userId: user.id
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to join tournament');
      }

      // Reload tournaments to update participant status
      loadTournaments();
      alert('Successfully joined tournament!');

    } catch (err) {
      console.error('Error joining tournament:', err);
      alert(err.message || 'Failed to join tournament');
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-500';
      case 'pending':
        return 'bg-yellow-500';
      case 'completed':
        return 'bg-gray-500';
      default:
        return 'bg-blue-500';
    }
  };

  const getBracketTypeLabel = (type) => {
    switch (type) {
      case 'single_elimination':
        return 'Single Elimination';
      case 'double_elimination':
        return 'Double Elimination';
      case 'swiss':
        return 'Swiss System';
      case 'round_robin':
        return 'Round Robin';
      default:
        return type;
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-sm text-muted-foreground">Loading tournaments...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-4" />
            <p className="text-red-500">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Show bracket view if tournament is selected
  if (selectedTournament) {
    return (
      <div>
        <button
          onClick={() => setSelectedTournament(null)}
          className="mb-4 flex items-center gap-2 text-primary hover:underline"
        >
          <Icon name="ArrowLeft" size={16} />
          Back to Tournaments
        </button>
        <TournamentBracket tournamentId={selectedTournament.id} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter tabs */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex gap-1 bg-muted rounded-lg p-1">
          {['active', 'upcoming', 'completed'].map(filterOption => (
            <button
              key={filterOption}
              onClick={() => setFilter(filterOption)}
              className={`flex-1 px-4 py-2 rounded text-sm font-medium transition-colors capitalize ${
                filter === filterOption
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {filterOption}
            </button>
          ))}
        </div>
      </div>

      {/* Tournaments list */}
      {tournaments.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-8 text-center">
          <Icon name="Trophy" size={64} className="text-muted-foreground mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Tournaments</h3>
          <p className="text-sm text-muted-foreground">
            {filter === 'active' && 'No active tournaments at the moment.'}
            {filter === 'upcoming' && 'No upcoming tournaments scheduled.'}
            {filter === 'completed' && 'No completed tournaments yet.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {tournaments.map(tournament => (
            <div
              key={tournament.id}
              className="bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-colors"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-foreground">{tournament.name}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold text-white ${getStatusColor(tournament.status)}`}>
                      {tournament.status}
                    </span>
                    {tournament.isParticipant && (
                      <span className="px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-semibold">
                        Joined
                      </span>
                    )}
                  </div>
                  {tournament.description && (
                    <p className="text-sm text-muted-foreground mb-3">{tournament.description}</p>
                  )}
                </div>
              </div>

              {/* Tournament details */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Format</p>
                  <p className="text-sm font-semibold text-foreground">{getBracketTypeLabel(tournament.bracket_type)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Participants</p>
                  <p className="text-sm font-semibold text-foreground">
                    {tournament.participantCount}{tournament.max_participants ? `/${tournament.max_participants}` : ''}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Starts</p>
                  <p className="text-sm font-semibold text-foreground">{formatDateTime(tournament.start_time)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Prize Pool</p>
                  <p className="text-sm font-semibold text-foreground">
                    {tournament.prize_pool?.coins ? `${tournament.prize_pool.coins} coins` : 'Glory'}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                {tournament.status === 'active' || tournament.status === 'completed' ? (
                  <button
                    onClick={() => setSelectedTournament(tournament)}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors flex items-center gap-2"
                  >
                    <Icon name="Trophy" size={16} />
                    View Bracket
                  </button>
                ) : null}

                {tournament.status === 'pending' && !tournament.isParticipant && user && (
                  <button
                    onClick={() => handleJoinTournament(tournament.id)}
                    disabled={tournament.max_participants && tournament.participantCount >= tournament.max_participants}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Icon name="UserPlus" size={16} />
                    Join Tournament
                  </button>
                )}

                {tournament.status === 'pending' && tournament.isParticipant && (
                  <div className="px-4 py-2 bg-green-500/10 text-green-500 rounded-lg font-medium flex items-center gap-2">
                    <Icon name="Check" size={16} />
                    Registered
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
